// Listbox.tsx
import { useState, useEffect, useRef } from "react";
import { clsx } from "clsx";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

interface DropdownProps<T extends string = string> {
  options: {
    id: T;
    text: string;
    disabled?: boolean;
    suffix?: React.ReactNode;
    prefix?: React.ReactNode;
    customStyle?: string;
  }[];
  searchable?: boolean;
  isMulti?: boolean;
  value?: T | T[];
  onChange?: (option: { id: T; text: string }) => void;
  className?: string;
  buttonClassName?: string;
  helperText?: string;
  disabled?: boolean;
  size?: 'small' | 'medium' | 'large';
  label?: string;
  placeholder?: string;
  isRequired?: boolean;
  error?: boolean;
  buttonContent?: React.ReactNode;
  dropdownWidth?: number;
  variant?: 'default' | 'clean' | 'inline';
  onInputChange?: (value: string) => void;
}

export const Dropdown = <T extends string>({ 
  options, 
  searchable = false, 
  value,
  onChange,
  className = '',
  buttonClassName = '',
  helperText,
  disabled = false,
  size = 'medium',
  label,
  placeholder = 'Select an option',
  isRequired = false,
  error = false,
  buttonContent,
  dropdownWidth,
  isMulti = false,
  variant = 'default',
  onInputChange
}: DropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState("");
  const [inputWidth, setInputWidth] = useState(80); // Default minimum width
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const textMeasureRef = useRef<HTMLSpanElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState<'top' | 'bottom'>('bottom');

  const sizeClasses = {
    small: 'px-3 py-2 text-sm text-[13px]',
    medium: 'px-4 py-3',
    large: 'px-4 py-4 text-base'
  };

  const itemSizeClasses = {
    small: 'px-3 py-2 text-[13px]',
    medium: 'px-4 py-3 text-base',
    large: 'px-4 py-4 text-base'
  };

  // Effect to update inputValue when the selected option changes
  useEffect(() => {
    if (variant === 'inline' && !isMulti) {
      const selected = options.find(opt => opt.id === value);
      if (selected) {
        setInputValue(selected.text);
        updateInputWidth(selected.text);
      } else {
        updateInputWidth("");
      }
    }
  }, [value, options, variant, isMulti]);

  // Calculate dropdown offset for inline variant
  const calculateDropdownOffset = () => {
    if (variant === 'inline' && inputWidth > 0) {
      // For very short inputs, provide a more reasonable offset
      if (inputWidth < 60) {
        return {
          left: `-120px`, // Fixed offset for very small inputs
        };
      }
      
      // Center the dropdown under the input while accounting for its width
      const offsetMultiplier = 150;
      return {
        left: `calc(${inputWidth / 2}px - ${offsetMultiplier}px)`,
      };
    }
    return {};
  };

  // Function to update input width based on content
  const updateInputWidth = (text: string) => {
    if (textMeasureRef.current) {
      textMeasureRef.current.textContent = text || placeholder;
      // For very short text, use a proportional width instead of a large minimum
      const contentWidth = textMeasureRef.current.offsetWidth;
      // Add a small buffer to make sure text fits well
      const newWidth = contentWidth + (text?.length <= 3 ? 8 : 15);
      setInputWidth(newWidth);
    }
  };

  const calculatePosition = () => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const spaceAbove = rect.top;
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = searchable ? 350 : 300; // Approximate height with/without search

      return spaceBelow >= dropdownHeight || spaceAbove < spaceBelow ? 'bottom' : 'top';
    }
    return 'bottom';
  };

  const toggleDropdown = () => {
    if (!isOpen) {
      // Calculate position before opening
      setDropdownPosition(calculatePosition());
      
      // If inline variant, focus the input when opening
      if (variant === 'inline' && inputRef.current) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 0);
      }
    }
    setIsOpen(!isOpen);
  };

  const filteredOptions = options.filter((option) => {
    if (variant === 'inline' && searchTerm) {
      return option.text.toLowerCase().includes(searchTerm.toLowerCase());
    }
    return option.text.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const handleOutsideClick = (event: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
      setIsOpen(false);
      
      // For inline variant, restore the selected option text
      if (variant === 'inline' && !isMulti) {
        const selected = options.find(opt => opt.id === value);
        if (selected) {
          setInputValue(selected.text);
        } else {
          setInputValue("");
        }
        setSearchTerm("");
      }
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  // Recalculate position if window is resized while open
  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        setDropdownPosition(calculatePosition());
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, searchable]);

  const selectedValues = isMulti && Array.isArray(value) ? value : [value as T];
  const selectedOptions = options.filter(opt => selectedValues.includes(opt.id));

  const handleOptionClick = (option: typeof options[0]) => {
    if (disabled || option.disabled) return;

    if (isMulti) {
      const newValues = selectedValues.includes(option.id)
        ? selectedValues.filter(v => v !== option.id)
        : [...selectedValues, option.id];
      onChange?.({ id: newValues.join(',') as T, text: option.text });
    } else {
      onChange?.(option);
      if (variant === 'inline') {
        setInputValue(option.text);
      }
      setIsOpen(false);
    }
    setSearchTerm("");
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setSearchTerm(value);
    updateInputWidth(value);
    
    if (!isOpen) {
      setIsOpen(true);
      setDropdownPosition(calculatePosition());
    }
    
    if (onInputChange) {
      onInputChange(value);
    }
  };

  // Handle keyboard events for the input field
  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && filteredOptions.length === 1) {
      // If there's exactly one match, select it
      handleOptionClick(filteredOptions[0]);
      e.preventDefault();
    }
  };

  const getButtonClasses = () => {
    if (variant === 'clean') {
      return clsx(
        'flex items-center min-w-[180px] justify-start sm:justify-end',
        {
          'cursor-not-allowed': disabled,
          'cursor-pointer': !disabled,
        },
        'focus:outline-none',
        buttonClassName
      );
    }

    if (variant === 'inline') {
      return clsx(
        'flex items-center',
        {
          'cursor-not-allowed': disabled,
          'cursor-pointer': !disabled,
        },
        'focus:outline-none',
        buttonClassName
      );
    }

    return clsx(
      'flex items-center justify-between w-full rounded-lg border bg-white',
      sizeClasses[size],
      {
        'cursor-not-allowed': disabled,
        'cursor-pointer': !disabled,
        'rounded-b-none border-b-0': isOpen && dropdownPosition === 'bottom',
        'rounded-t-none border-t-0': isOpen && dropdownPosition === 'top',
        'border-red-300': error,
        'border-gray-300': !error,
      },
      'shadow-xs focus:border-primary-base focus:outline-hidden focus:ring-1 focus:ring-primary-base',
      buttonClassName
    );
  };

  // Determine if parent has larger-than-base font size (for h1-h9 headings)

  return (
    <div
      ref={dropdownRef}
      className={clsx('relative flex flex-col', variant === 'default' ? 'max-w-[343px]' : '', className, {
        'opacity-50': disabled,
        'inline-block': variant === 'inline',
      })}
      // Force all font properties to be inherited directly from parent
      style={variant === 'inline' ? {
        fontSize: 'inherit',
        fontWeight: 'inherit',
        lineHeight: 'inherit',
        fontFamily: 'inherit',
        color: 'inherit'
      } : {}}
    >
      {/* Hidden span for measuring text width */}
      {variant === 'inline' && (
        <span 
          ref={textMeasureRef} 
          className="absolute opacity-0 pointer-events-none whitespace-nowrap"
          style={{ 
            fontSize: 'inherit',
            fontWeight: 'inherit',
            lineHeight: 'inherit',
            fontFamily: 'inherit',
            position: 'absolute', 
            left: '-9999px' 
          }}
        ></span>
      )}

      {variant === 'default' && label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
          {isRequired && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        {/* Dropdown Trigger */}
        {variant === 'inline' ? (
          <div className={getButtonClasses()}>
            <div className="inline-block border-b-2 border-gray-900">
              <input
                ref={inputRef}
                type="text"
                value={isOpen ? inputValue : (selectedOptions[0]?.text || inputValue)}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                placeholder={placeholder}
                className={clsx(
                  "bg-transparent border-none outline-none focus:outline-none p-0 w-auto",
                  {
                    "text-center": inputWidth < 60 && !selectedOptions[0]?.text,
                    "text-gray-900": selectedOptions[0]?.text || isOpen,
                    "text-gray-500 italic": !selectedOptions[0]?.text && !isOpen, // Style placeholder as gray italic
                  }
                )}
                style={{ 
                  width: `${inputWidth}px`,
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                  lineHeight: 'inherit',
                  fontFamily: 'inherit',
                  color: 'inherit'
                }}
                onClick={() => !disabled && toggleDropdown()}
                disabled={disabled}
              />
            </div>
            <div 
              className="cursor-pointer inline-flex"
              style={{ marginLeft: '0.25em' }}
              onClick={() => !disabled && toggleDropdown()}
            >
              <img
                src="https://cdn.builder.io/api/v1/image/assets/TEMP/e0e66a9818b5d21ad593329109be78a77cb6c3415b9e8b4a3cc75d24ae430ca1"
                alt="Toggle dropdown"
                className={clsx({
                  'transform rotate-180': isOpen
                })}
                style={{
                  width: 'auto',
                  height: '0.7em', // This makes it scale with the font size
                  maxHeight: '1.25em',
                  minHeight: '0.7em'
                }}
              />
            </div>
          </div>
        ) : (
          <div
            role="button"
            tabIndex={disabled ? -1 : 0}
            onClick={() => !disabled && toggleDropdown()}
            onKeyPress={(e) => {
              if (!disabled && (e.key === "Enter" || e.key === " ")) toggleDropdown();
            }}
            className={getButtonClasses()}
            aria-expanded={isOpen}
            aria-haspopup="listbox"
            aria-disabled={disabled}
          >
            {variant === 'clean' ? (
              <div className="flex items-center">
                <div className="flex items-center">
                  <span className={`${selectedOptions[0] ? "text-gray-900" : "text-gray-400"} truncate max-w-[220px]`}> 
                    {selectedOptions[0]?.text || placeholder}
                  </span>
                  {selectedOptions[0]?.suffix && (
                    <div className="ml-2">
                      {selectedOptions[0].suffix}
                    </div>
                  )}
                </div>
                <ChevronDownIcon className={clsx('w-5 h-5 ml-2', {
                  'transform rotate-180': isOpen
                })} />
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between w-full">
                  {buttonContent || (
                    <div className="flex items-center">
                      {isMulti ? (
                        selectedOptions.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {selectedOptions.map(opt => (
                              <span key={opt.id} className="bg-primary-50 text-primary-700 px-2 py-1 rounded-full text-sm whitespace-nowrap">
                                {opt.text}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="text-gray-400">{placeholder}</span>
                        )
                      ) : (
                        <div className="flex items-center">
                          <span className="text-gray-900"> 
                            {selectedOptions[0]?.text || placeholder}
                          </span>
                          {!buttonContent && selectedOptions[0]?.suffix && (
                            <div className="ml-2">
                              {selectedOptions[0].suffix}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <img
                  src="https://cdn.builder.io/api/v1/image/assets/TEMP/e0e66a9818b5d21ad593329109be78a77cb6c3415b9e8b4a3cc75d24ae430ca1"
                  alt="Toggle dropdown"
                  className={clsx(size === 'small' ? 'w-5 h-5' : 'w-6 h-6', 'ml-2', {
                    'transform rotate-180': isOpen
                  })}
                />
              </>
            )}
          </div>
        )}

        {/* Dropdown Content */}
        {!disabled && isOpen && (
          <div
            role="listbox"
            className={clsx(
              'absolute z-100 bg-white rounded-lg shadow-lg border border-gray-300',
              dropdownPosition === 'bottom' ? 'top-[100%]' : 'bottom-[100%]',
              {
                'rounded-t-none border-t-0': dropdownPosition === 'bottom' && variant !== 'clean' && variant !== 'inline',
                'rounded-b-none border-b-0': dropdownPosition === 'top' && variant !== 'clean' && variant !== 'inline',
                'sm:right-0': variant === 'clean', // Position dropdown to the right only on sm screens and up
                'left-0': variant === 'clean', // Position dropdown to the left on mobile
                'mt-1': variant === 'inline',
                'text-base': variant === 'inline', // Reset font size to base for dropdown options
              }
            )}
            style={{ 
              width: dropdownWidth ? `${dropdownWidth}px` : variant === 'clean' ? 'auto' : '100%', 
              minWidth: variant === 'clean' ? '280px' : (variant === 'inline' ? '300px' : 'auto'),
              ...(variant === 'inline' ? calculateDropdownOffset() : {}),
              ...(variant === 'inline' ? { fontSize: '1rem' } : {}), // Force 1rem font size for inline variant
            }}
          >
            {variant !== 'clean' && variant !== 'inline' && <div className="w-full h-[1px] bg-gray-300" />}
            {searchable && variant !== 'inline' && (
              <div className="px-2 py-1 border-b border-gray-200">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg shadow-xs focus:border-primary-base focus:outline-hidden focus:ring-1 focus:ring-primary-base"
                />
              </div>
            )}
            <div className="max-h-60 overflow-y-auto">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, index) => (
                  <div
                    key={index}
                    role="option"
                    aria-selected={selectedValues.includes(option.id)}
                    tabIndex={0}
                    onClick={() => handleOptionClick(option)}
                    onKeyDown={(e) => {
                      if (!option.disabled && (e.key === "Enter" || e.key === " ")) {
                        handleOptionClick(option);
                      }
                    }}
                    className={clsx(
                      'flex items-center w-full px-4 py-2',
                      variant === 'inline' ? 'py-2.5' : itemSizeClasses[size],
                      'text-gray-700 hover:bg-gray-100',
                      'cursor-pointer',
                      {
                        'bg-gray-100': selectedValues.includes(option.id),
                        'text-gray-400 cursor-not-allowed': option.disabled,
                        'text-base': variant === 'inline' // Always use text-base for dropdown options
                      }
                    )}
                  >
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center">
                        {option.prefix}
                        <span className={clsx(
                          option.disabled ? 'text-gray-400' : 'text-gray-900',
                          'truncate',
                          variant === 'inline' ? 'max-w-[260px]' : ''
                        )}>
                          {option.text}
                        </span>
                      </div>
                      {selectedValues.includes(option.id) && (
                        <svg className="w-5 h-5 text-primary-600" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                      {option.suffix}
                    </div>
                  </div>
                ))
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500">No options found</div>
              )}
            </div>
          </div>
        )}
      </div>

      {variant === 'default' && helperText && (
        <p className={clsx('mt-2 text-sm', error ? 'text-red-500' : 'text-gray-500')}>{helperText}</p>
      )}
    </div>
  );
};